import { get_user_profile, fetch_and_store_user_profile, get_token } from '@/router';

export default {
        handleBreadcrumbClick(id) {
            if (id === "home") {
                this.$router.push("/home");
            }
            if (id === "profile") {
                this.$router.push("/profile");
            }
            if (id === "user_management") {
                this.$router.push("/user_management");
            }
        },

        set_breadcrumb() {
            this.breadcrumb = this.is_managed_user_edit
                ? [
                    { id: "home", title: "Dashboard" },
                    { id: "user_management", title: "User Management" },
                    { id: "edit", title: "Edit User" }
                ]
                : [
                    { id: "home", title: "Dashboard" },
                    { id: "profile", title: "Profile" },
                    { id: "edit", title: "Edit Profile" }
                ];
        },

        set_profile_edit(profile = {}) {
            this.form.first_name = `${profile.first_name || ""}`.trim();
            this.form.last_name = `${profile.last_name || ""}`.trim();
            this.form.email = `${profile.email || ""}`.trim();
            this.form.phone = profile.phone_number || "";
        this.form.session_policy = profile.session_policy || profile.profile?.session_policy || "default";
        this.form.session_timeout_minutes = profile.session_timeout_minutes ?? profile.profile?.session_timeout_minutes ?? null;
        },

        parse_user_id(raw_user_id) {
            const user_id_text = String(raw_user_id || "").trim();

            if (!/^\d+$/.test(user_id_text)) {
                return null;
            }

            const user_id = Number(user_id_text);
            return Number.isSafeInteger(user_id) && user_id > 0 ? user_id : null;
        },

        get_selected_profile_id(profile) {
            if (this.selected_user_id) {
                return this.selected_user_id;
            }

            return this.parse_user_id(profile && (profile.id || profile.user_id));
        },

        get_endpoint_url(base_url, user_id, action = "") {
            const safe_user_id = this.parse_user_id(user_id);
            const normalized_base_url = String(base_url || "").trim();

            if (!safe_user_id || !normalized_base_url) {
                return "";
            }

            const base_with_slash = normalized_base_url.endsWith("/")
                ? normalized_base_url
                : `${normalized_base_url}/`;
            const safe_action = action === "update" ? "update/" : "";

            return `${base_with_slash}${safe_user_id}/${safe_action}`;
        },

        get_auth_headers(include_json_content_type = true) {
            const auth_token = get_token();
            const headers = {
                "Accept": "application/json"
            };

            if (include_json_content_type) {
                headers["Content-Type"] = "application/json";
            }

            if (auth_token) {
                headers.Authorization = `Token ${auth_token}`;
            }

            return headers;
        },

        extract_collection(response_data, collection_keys = ["data", "results", "users"]) {
            if (Array.isArray(response_data)) {
                return response_data;
            }

            return this.extract_nested_collection(response_data, collection_keys);
        },

        extract_nested_collection(response_data, collection_keys) {
            if (!response_data || typeof response_data !== "object") return [];

            for (const key of collection_keys) {
                const value = response_data[String(key)];
                const collection = this.get_collection_value(value, collection_keys);

                if (collection.length) return collection;
            }

            return [];
        },

        get_collection_value(value, collection_keys) {
            if (Array.isArray(value)) return value;
            if (!value || typeof value !== "object") return [];

            return this.extract_collection(value, collection_keys);
        },

        find_user_by_id(response_data, user_id) {
            const safe_user_id = this.parse_user_id(user_id);
            const users = this.extract_collection(response_data);

            if (users.length) {
                return users.find((user) => this.parse_user_id(user?.id || user?.user_id) === safe_user_id) || null;
            }

            return this.parse_user_id(response_data?.id || response_data?.user_id) === safe_user_id
                ? response_data
                : null;
        },

        get_selected_user_from_storage() {
            return JSON.parse(sessionStorage.getItem("edit_user_profile") || "null");
        },

        get_return_path() {
            return this.$route.query.return_to === "user_management"
                ? "/user_management"
                : "/profile";
        },

        get_first_error_value(response_data) {
            return response_data?.errors
                || response_data?.detail
                || response_data?.error
                || response_data?.message
                || response_data;
        },

        format_object_error(error_data) {
            return Object.entries(error_data)
                .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(", ") : messages}`)
                .join(" | ");
        },

        get_error_message(response_data, fallback_message) {
            const error_data = this.get_first_error_value(response_data);

            if (!error_data) return fallback_message;
            if (typeof error_data === "string") return error_data;
            if (Array.isArray(error_data)) return error_data.join(", ");
            if (typeof error_data === "object") return this.format_object_error(error_data);

            return fallback_message;
        },

        async get_response_data(response, fallback_message) {
            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(this.get_error_message(data, fallback_message));
            }

            return data;
        },

        async load_managed_user(user_id) {
            const safe_user_id = this.parse_user_id(user_id);
            const stored_user = this.get_selected_user_from_storage();
            const stored_user_id = this.parse_user_id(stored_user && (stored_user.id || stored_user.user_id));

            if (stored_user && stored_user_id === safe_user_id) {
                return stored_user;
            }

            if (!safe_user_id) {
                throw new Error("Unable to load user details.");
            }

            throw new Error("Unable to load user details.");
        },

        async load_profile_phone(profile, user_id) {
            const safe_user_id = this.parse_user_id(user_id);

            if (!safe_user_id || !this.update_user_url || !get_token()) {
                if (!this.update_user_url) {
                    console.warn("VUE_APP_UPDATE_USER_URL is not configured.");
                }
                return profile;
            }

            try {
                const profile_request = new Request(this.get_endpoint_url(this.update_user_url, safe_user_id), {
                    method: "GET",
                    headers: this.get_auth_headers(false)
                });
                const response = await fetch(profile_request);

                if (response.status === 200) {
                    const response_data = await response.json();
                    const profile_data = this.find_user_by_id(response_data, safe_user_id) || response_data;
                    profile.phone_number = profile_data.phone_number;
                }
            } catch (error) {
                console.log(`Failed to fetch phone number: ${error}`);
            }

            return profile;
        },

        async resolve_profile_for_edit() {
            const profile = get_user_profile();

            if (this.is_managed_user_edit) {
                return this.load_managed_user(this.selected_user_id);
            }

            return profile || fetch_and_store_user_profile();
        },

        async populate_profile_form(profile) {
            if (!profile) {
                throw new Error("Failed to load user profile");
            }

            const user_id = this.get_selected_profile_id(profile);
            const next_profile = user_id && get_token()
                ? await this.load_profile_phone(profile, user_id)
                : profile;

            this.set_profile_edit(next_profile);
        },

        handle_profile_load_error(error) {
            this.form_error = error.message || "Failed to load profile data. Please try again.";
            console.error("Profile loading error:", error);

            if (!this.is_managed_user_edit) {
                this.set_profile_edit(get_user_profile());
            }
        },

        async loadUserProfile() {
            this.is_loading = true;
            this.form_error = "";

            try {
                const profile = await this.resolve_profile_for_edit();
                await this.populate_profile_form(profile);
            } catch (error) {
                this.handle_profile_load_error(error);
            } finally {
                this.is_loading = false;
            }
        }
};
